import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Form, Alert, Spinner, Table, Badge, Row, Col, Card } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './InvoiceList.css';

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [families, setFamilies] = useState([]); // Ajout de l'état pour les familles
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serverErrorDetails, setServerErrorDetails] = useState('');

  // Charger les données
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        setServerErrorDetails('');

        console.log('🔄 Chargement des données...');

        // Essayer de charger les facture
        try {
          const invoicesRes = await axios.get('/api/Invoices/getInvoices', {
            timeout: 8000
          });

          if (invoicesRes.data && invoicesRes.data.invoices) {
            setInvoices(invoicesRes.data.invoices);
            setFilteredInvoices(invoicesRes.data.invoices);
            console.log(`✅ ${invoicesRes.data.invoices.length} factures chargées`);
          }
        } catch (invoiceError) {
          console.error('Erreur factures:', invoiceError.response?.data);
          setServerErrorDetails(invoiceError.response?.data?.error || 'Erreur inconnue');
          throw invoiceError;
        }

        // Charger les familles pour lookup d'ingrédients
        try {
          const familiesRes = await axios.get('/api/ingredient/families');
          if (familiesRes.data) {
            setFamilies(familiesRes.data);
            console.log(`✅ ${familiesRes.data.length} familles chargées pour lookup`);
          }
        } catch (err) {
          console.warn('Erreur chargement familles:', err);
        }

        // Essayer de charger les fournisseurs
        try {
          const suppliersRes = await axios.get('/api/suppliers/getSuppliers', {
            timeout: 5000
          });

          if (suppliersRes.data && suppliersRes.data.suppliers) {
            setSuppliers(suppliersRes.data.suppliers);
            console.log(`✅ ${suppliersRes.data.suppliers.length} fournisseurs chargés`);
          }
        } catch (supplierError) {
          console.warn('Erreur fournisseurs:', supplierError.message);
          // On continue sans fournisseurs
        }

      } catch (err) {
        console.error('❌ Erreur complète:', err);
        setError('Erreur de chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Obtenir le nom de l'ingrédient de manière robuste
  const getIngredientName = (item) => {
    if (!item) return "N/A";

    // 1. Si déjà peuplé par le serveur
    if (item.ingredient?.name) return item.ingredient.name;

    // 2. Chercher dans les familles chargées localement
    const ingredientId = item.ingredient?._id || item.ingredient;

    if (ingredientId && families.length > 0) {
      const idStr = ingredientId.toString();
      for (const fam of families) {
        if (fam.ingredients && Array.isArray(fam.ingredients)) {
          const found = fam.ingredients.find(ing =>
            (ing._id && ing._id.toString() === idStr) ||
            (ing.id && ing.id.toString() === idStr)
          );
          if (found) return found.name;
        }
      }
    }

    // Fallback : si on a l'ID mais pas le nom, on affiche un bout de l'ID pour debug
    if (ingredientId) {
      return `Article (${ingredientId.toString().substring(18)})`;
    }

    return "Article";
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "N/A";
    }
  };

  // Obtenir le nom du fournisseur
  const getSupplierName = (supplierData) => {
    if (!supplierData) return "N/A";

    // Si c'est un objet avec companyName (déjà peuplé)
    if (typeof supplierData === 'object' && supplierData.companyName) {
      return supplierData.companyName;
    }

    // Sinon chercher dans la liste
    const supplier = suppliers.find(s => {
      if (typeof supplierData === 'object' && supplierData._id) {
        return s._id === supplierData._id;
      }
      return s._id === supplierData;
    });

    return supplier ? supplier.companyName : "Fournisseur inconnu";
  };

  // Obtenir le nom de la localisation
  const getLocationName = (locationData) => {
    if (!locationData) return "N/A";
    if (typeof locationData === 'object' && locationData.name) {
      return locationData.name;
    }
    return "Localisation inconnue";
  };

  // Calculer le total
  const calculateInvoiceTotal = (invoice) => {
    return invoice.totalAmount || 0;
  };

  // Filtrer les factures
  useEffect(() => {
    let temp = [...invoices];

    // Recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      temp = temp.filter(inv => {
        const supplierName = getSupplierName(inv.supplier).toLowerCase();
        const invoiceId = (inv._id || '').toString().toLowerCase();
        const hasIngredientMatch = inv.items?.some(item =>
          getIngredientName(item).toLowerCase().includes(term)
        );
        return supplierName.includes(term) || invoiceId.includes(term) || hasIngredientMatch;
      });
    }

    // Filtre par fournisseur
    if (selectedSupplier) {
      temp = temp.filter(inv => {
        const supplier = inv.supplier;
        const supplierId = supplier?._id || supplier;
        return supplierId === selectedSupplier;
      });
    }

    setFilteredInvoices(temp);
  }, [selectedSupplier, searchTerm, invoices, suppliers, families]);

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSelectedSupplier('');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
  };

  // Recharger
  const handleReload = () => {
    window.location.reload();
  };

  // Générer PDF (version simplifiée)
  const handleGeneratePDF = (invoice) => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.text("FACTURE", 105, 20, { align: "center" });

      const invoiceId = invoice._id ? invoice._id.substring(0, 8).toUpperCase() : 'N/A';
      const date = formatDate(invoice.date);
      const supplier = getSupplierName(invoice.supplier);
      const location = getLocationName(invoice.localisation);

      doc.setFontSize(10);
      doc.text(`Facture N°: ${invoiceId}`, 14, 35);
      doc.text(`Date: ${date}`, 14, 42);
      doc.text(`Fournisseur: ${supplier}`, 14, 49);
      doc.text(`Localisation: ${location}`, 14, 56);

      // Tableau des articles
      const tableData = [];
      if (invoice.items && Array.isArray(invoice.items)) {
        invoice.items.forEach((item, idx) => {
          const ingredientName = getIngredientName(item);
          const quantity = item.quantity || 0;
          const unit = item.unit || "unité";
          const price = (item.price || 0).toFixed(3);
          const total = (item.total || 0).toFixed(3);

          tableData.push([
            idx + 1,
            ingredientName.substring(0, 40),
            `${quantity} ${unit}`,
            `${price} DT`,
            `${total} DT`
          ]);
        });
      }

      autoTable(doc, {
        head: [['#', 'Article', 'Quantité', 'Prix HT', 'Total']],
        body: tableData,
        startY: 65,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 9 }
      });

      // Total
      const finalY = doc.lastAutoTable.finalY || 100;
      doc.setFontSize(12);
      const totalAmount = calculateInvoiceTotal(invoice);
      doc.text(`Total: ${totalAmount.toFixed(3)} DT`, 160, finalY + 10);

      doc.save(`Facture_${invoiceId}.pdf`);
      alert(`PDF généré: Facture_${invoiceId}.pdf`);

    } catch (error) {
      console.error('Erreur PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  // Basculer les détails
  const toggleInvoiceDetails = (invoiceId) => {
    setExpandedInvoice(expandedInvoice === invoiceId ? null : invoiceId);
  };

  // Calculer les statistiques
  const calculateStatistics = () => {
    const totalInvoices = filteredInvoices.length;
    const totalItems = filteredInvoices.reduce((sum, inv) =>
      sum + (Array.isArray(inv.items) ? inv.items.length : 0), 0);
    const totalAmount = filteredInvoices.reduce((sum, inv) =>
      sum + calculateInvoiceTotal(inv), 0);

    return { totalInvoices, totalItems, totalAmount };
  };

  const stats = calculateStatistics();

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-3">Connexion au serveur...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <h2 className="mb-4">📋 Liste des Factures</h2>

      {/* Affichage des erreurs */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>⚠️ Erreur Backend</Alert.Heading>
          <p><strong>{error}</strong></p>

          {serverErrorDetails && (
            <div className="mt-3">
              <p className="mb-1"><strong>Détails de l'erreur :</strong></p>
              <div className="bg-dark text-white p-2 rounded small">
                <code>{serverErrorDetails}</code>
              </div>
            </div>
          )}

          <div className="mt-3">
            <p><strong>Solution rapide :</strong></p>
            <ol className="mb-3">
              <li>Vérifiez que votre fichier <code>models/Supplier.js</code> existe</li>
              <li>Assurez-vous qu'il exporte le modèle correctement : <code>module.exports = mongoose.model('Supplier', SupplierSchema)</code></li>
              <li>Vérifiez que le modèle est importé dans le contrôleur : <code>const Supplier = require('../models/Supplier')</code></li>
              <li>Redémarrez le serveur backend</li>
            </ol>
          </div>

          <div className="d-flex gap-2 mt-2">
            <Button variant="outline-danger" onClick={handleReload} size="sm">
              🔄 Réessayer
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => window.open('http://localhost:3000/api/Invoices/getInvoices', '_blank')}
              size="sm"
            >
              📡 Tester l'API
            </Button>
          </div>
        </Alert>
      )}

      {/* Statistiques (seulement si pas d'erreur) */}
      {!error && invoices.length > 0 && (
        <Row className="mb-4">
          <Col md={4}>
            <Card className="stat-card">
              <Card.Body>
                <Card.Title>Factures</Card.Title>
                <h3>{stats.totalInvoices}</h3>
                <Card.Text>Total</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="stat-card">
              <Card.Body>
                <Card.Title>Articles</Card.Title>
                <h3>{stats.totalItems}</h3>
                <Card.Text>Total</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="stat-card">
              <Card.Body>
                <Card.Title>Montant Total</Card.Title>
                <h3>{stats.totalAmount.toFixed(3)} DT</h3>
                <Card.Text>Valeur totale</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtres (seulement si données) */}
      {!error && invoices.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Filtres</Card.Title>
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Recherche</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Fournisseur ou ID..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fournisseur</Form.Label>
                  <Form.Select
                    value={selectedSupplier}
                    onChange={e => setSelectedSupplier(e.target.value)}
                    disabled={suppliers.length === 0}
                  >
                    <option value="">Tous les fournisseurs</option>
                    {suppliers.map(s => (
                      <option key={s._id} value={s._id}>
                        {s.companyName || s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Date de début</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Date de fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    min={startDate}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="mt-3">
              <Button variant="outline-secondary" onClick={resetFilters}>
                🔄 Réinitialiser
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Tableau des factures */}
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead className="table-dark">
            <tr>
              <th>ID Facture</th>
              <th>Date</th>
              <th>Fournisseur</th>
              <th>Localisation</th>
              <th>Articles</th>
              <th>Montant Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!error && filteredInvoices.length > 0 ? (
              filteredInvoices.map(inv => (
                <React.Fragment key={inv._id}>
                  <tr>
                    <td>
                      <Badge bg="light" text="dark">
                        {inv._id ? inv._id.substring(0, 8).toUpperCase() : 'N/A'}
                      </Badge>
                    </td>
                    <td>{formatDate(inv.date)}</td>
                    <td>{getSupplierName(inv.supplier)}</td>
                    <td>{getLocationName(inv.localisation)}</td>
                    <td>
                      {inv.items && Array.isArray(inv.items) ? (
                        <div className="small text-truncate" style={{ maxWidth: '200px' }}>
                          {inv.items.map(item => getIngredientName(item)).join(', ')}
                        </div>
                      ) : '0'}
                    </td>
                    <td>
                      <strong>{calculateInvoiceTotal(inv).toFixed(3)} DT</strong>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => toggleInvoiceDetails(inv._id)}
                        >
                          {expandedInvoice === inv._id ? 'Masquer' : 'Détails'}
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleGeneratePDF(inv)}
                        >
                          📄 PDF
                        </Button>
                      </div>
                    </td>
                  </tr>

                  {expandedInvoice === inv._id && (
                    <tr>
                      <td colSpan="7">
                        <Card className="mt-2">
                          <Card.Body>
                            <h6>📦 Détails de la facture</h6>
                            {inv.items && Array.isArray(inv.items) && inv.items.length > 0 ? (
                              <Table size="sm">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Article</th>
                                    <th>Quantité</th>
                                    <th>Unité</th>
                                    <th>Prix HT</th>
                                    <th>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {inv.items.map((item, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td>
                                        {getIngredientName(item)}
                                      </td>
                                      <td>{item.quantity}</td>
                                      <td>{item.unit || ''}</td>
                                      <td>{item.price?.toFixed(3) || '0.000'} DT</td>
                                      <td>
                                        <strong>{item.total?.toFixed(3) || '0.000'} DT</strong>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            ) : (
                              <Alert variant="warning">Aucun article</Alert>
                            )}
                          </Card.Body>
                        </Card>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  {error
                    ? 'Erreur de connexion au serveur'
                    : invoices.length === 0
                      ? 'Aucune facture disponible'
                      : 'Aucune facture ne correspond aux filtres'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default InvoiceList;